/**
 * Domain-shortcut chips shown before the user's first message.
 *
 * Sourced from the 24-segment catalog (`SEGMENTS` filtered to
 * `status === 'ready'`), so the chips reflect the products we
 * actually sell rather than the four hand-curated demo categories
 * the legacy strip used. The current ready set is ~11 segments
 * (Music, Video, Photography, Industry, Healthcare, Finance,
 * Government, Legal, Education, Cybersecurity, Defense).
 *
 * Click semantics:
 *   - If the segment maps to one of the four scripted Coach trees
 *     (`food` / `medical` / `retail` / `office`), we short-circuit
 *     and jump straight to that tree's Q1 via `onPick`.
 *   - Otherwise we send the localized segment name as a free-form
 *     first message via `onSendText`. The Coach engine's
 *     `onFirstMessage` keyword-detection then either lands on a
 *     matching tree or returns the disambiguate prompt — graceful
 *     degradation, no crash, and the user gets a relevant follow-up.
 *
 * The four legacy `quickstart.<id>` labels in `coach.json` remain
 * because `handlePickDomain` in CoachPage uses them for the user
 * bubble when a tree-mapped chip is clicked.
 */

import { useTranslation } from 'react-i18next';

import { DomainIcon } from '@/components/DomainIcon';
import { SEGMENTS } from '@/data/segments';
import type { NormalizedDomain } from '@/api/types';

import type { CoachDomain } from './trees';

/**
 * Segments whose subject matter overlaps cleanly with one of the
 * four scripted trees. Only the unambiguous mappings are listed —
 * an over-eager mapping (e.g. `industry → food`) would route a user
 * who clicked "Industry" into a bakery dialogue, which is worse than
 * letting the engine disambiguate.
 */
const SEGMENT_TO_TREE: Partial<Record<string, CoachDomain>> = {
  healthcare: 'medical',
  finance: 'office',
  // Phase 4.8 — direct mappings for the 9 new dialogue trees so the
  // chip click jumps to the matching Q1 instead of bouncing through
  // disambiguate. Segment slugs come from `ui/src/data/segments.ts`.
  government: 'government',
  legal: 'legal',
  education: 'education',
  cybersecurity: 'cybersecurity',
  industry: 'industry',
  music: 'music',
  video: 'video',
  photography: 'photography',
  defense: 'defense',
};

const READY_SEGMENTS = SEGMENTS.filter((s) => s.status === 'ready');

export function QuickStartChips({
  onPick,
  onSendText,
}: {
  /** Used when the chip maps to a scripted tree — instant Q1 jump. */
  onPick: (id: CoachDomain) => void;
  /** Used when no tree maps; sends the chip label as a normal user
   *  message so the engine can keyword-detect or disambiguate. */
  onSendText: (label: string) => void;
}) {
  const { t } = useTranslation('coach');
  return (
    <div className="flex flex-wrap gap-2">
      {READY_SEGMENTS.map((s) => {
        const tree = SEGMENT_TO_TREE[s.id];
        const label = t(`segments:${s.id}.name`, { defaultValue: s.id });
        // EngineDomain is PascalCase ('Medical'); DomainIcon and the
        // text-domain-* Tailwind palette want the lowercase form.
        const normalized = s.engineDomain.toLowerCase() as NormalizedDomain;
        return (
          <button
            key={s.id}
            type="button"
            onClick={() => (tree ? onPick(tree) : onSendText(label))}
            className="inline-flex items-center gap-2 rounded-full border border-border bg-surface px-3 py-1.5 text-xs text-ink transition-colors hover:border-spark hover:bg-spark-soft"
          >
            <span
              className={`flex h-4 w-4 items-center justify-center text-domain-${normalized}`}
            >
              <DomainIcon domain={normalized} className="h-4 w-4" />
            </span>
            {label}
          </button>
        );
      })}
    </div>
  );
}
